/**
 * Custom BTR-branded property blueprint visual — dark charcoal ground,
 * red outline, risk-documentation markers, and a scanning sweep.
 * Fully scalable SVG.
 */
export default function Blueprint({ className = "", showLabel = true }) {
  return (
    <div className={`relative w-full h-full overflow-hidden rounded-md bg-[#0a0a0a] ${className}`}>
      {/* Grid */}
      <div className="absolute inset-0 btr-blueprint-overlay" />

      {/* SVG Property outline */}
      <svg
        viewBox="0 0 400 500"
        preserveAspectRatio="xMidYMid meet"
        className="absolute inset-0 w-full h-full"
        fill="none"
        stroke="currentColor"
        aria-hidden
      >
        <g stroke="#a2020b" strokeWidth="1.3">
          {/* Outer property footprint */}
          <path d="M 60 430 L 60 180 L 120 180 L 120 110 L 280 110 L 280 180 L 340 180 L 340 430 Z" />
          {/* Roof ridge line */}
          <path d="M 120 110 L 200 60 L 280 110" strokeDasharray="3 4" opacity="0.6" />
          {/* Interior partitions */}
          <path d="M 60 300 L 340 300" opacity="0.55" />
          <path d="M 200 300 L 200 430" opacity="0.45" />
          <path d="M 60 240 L 200 240" opacity="0.35" />
          {/* Windows */}
          <path d="M 85 220 L 105 220" strokeWidth="2.2" />
          <path d="M 145 220 L 175 220" strokeWidth="2.2" />
          <path d="M 225 220 L 255 220" strokeWidth="2.2" />
          <path d="M 295 220 L 315 220" strokeWidth="2.2" />
          {/* Front door */}
          <path d="M 185 430 L 185 400 L 215 400 L 215 430" strokeWidth="1.6" />
          {/* Exterior border heavy */}
          <path d="M 60 430 L 340 430" strokeWidth="2" />
        </g>

        {/* Dimension ticks */}
        <g stroke="#a2020b" strokeWidth="0.8" opacity="0.55">
          <path d="M 60 455 L 340 455" />
          <path d="M 60 452 L 60 458" />
          <path d="M 340 452 L 340 458" />
          <path d="M 200 452 L 200 458" />
        </g>

        {/* Risk markers */}
        <g>
          {/* NE corner flag */}
          <circle cx="330" cy="120" r="4" fill="#a2020b" />
          <circle cx="330" cy="120" r="8" fill="none" stroke="#a2020b" strokeWidth="0.8" opacity="0.6" />
          {/* SW corner flag */}
          <circle cx="90" cy="390" r="4" fill="#a2020b" />
          <circle cx="90" cy="390" r="8" fill="none" stroke="#a2020b" strokeWidth="0.8" opacity="0.6" />
          {/* Mid window flag */}
          <rect x="233" y="208" width="14" height="14" fill="none" stroke="#a2020b" strokeWidth="1" />
          <path d="M 240 208 L 240 196" stroke="#a2020b" strokeWidth="0.8" />
          <circle cx="240" cy="194" r="1.6" fill="#a2020b" />
        </g>

        {/* Compass (NSEW) */}
        <g transform="translate(305, 60)" opacity="0.7">
          <circle r="16" fill="none" stroke="#a2020b" strokeWidth="0.8" />
          <path d="M 0 -14 L 0 14 M -14 0 L 14 0" stroke="#a2020b" strokeWidth="0.6" />
          <path d="M 0 -14 L -3 -6 L 0 -8 L 3 -6 Z" fill="#a2020b" />
          <text x="0" y="-20" fill="#a2020b" fontSize="7" textAnchor="middle" fontFamily="Outfit, sans-serif" fontWeight="700">N</text>
        </g>

        {/* Coordinates text */}
        <g fill="#a2020b" fontFamily="Outfit, sans-serif" fontSize="7" opacity="0.75">
          <text x="60" y="472" letterSpacing="2">A1 — 27.9506° N</text>
          <text x="280" y="472" letterSpacing="2">B4 — 82.4572° W</text>
        </g>
      </svg>

      {/* Scanning sweep */}
      <div className="btr-blueprint-scan" />

      {/* Corner brackets */}
      <div className="absolute top-2 left-2 w-5 h-5 border-t border-l border-[#a2020b]/70" />
      <div className="absolute top-2 right-2 w-5 h-5 border-t border-r border-[#a2020b]/70" />
      <div className="absolute bottom-2 left-2 w-5 h-5 border-b border-l border-[#a2020b]/70" />
      <div className="absolute bottom-2 right-2 w-5 h-5 border-b border-r border-[#a2020b]/70" />

      {/* Top-right status */}
      {showLabel && (
        <div className="absolute top-2.5 right-2.5 flex items-center gap-1.5 text-[8px] font-display tracking-[0.2em] uppercase text-[#a2020b]">
          <span className="w-1 h-1 rounded-full bg-[#a2020b] btr-red-glow" />
          Scan Active
        </div>
      )}
    </div>
  );
}
